import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, ModalHandle, SubTopBar } from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Link } from 'react-router-dom';
import {
    Button,
    ButtonGroup,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledButtonDropdown,
} from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { TicketModal } from '../Core/TicketModal';
import { UnitsStore } from '../Units/UnitsStore';
import { DisplaysStore } from './DisplaysStore';

export interface IDisplayViewNavProps {
    readonly serialNumber: string;
    readonly location: any;
}

@autoinject
@observer
export class DisplayViewNav extends React.Component<IDisplayViewNavProps> {
    public render() {
        const location = `/displays/${this.props.serialNumber}`;
        const chartsActive =
            this.props.location.pathname.includes('live-charts') || this.props.location.pathname.includes('min-max');

        return (
            <SubTopBar hide={this.appStore.hiddenHeader}>
                <ul className="nav flex-items-center">
                    <li className="nav-item mx-2">
                        <LinkContainer exact to={location} className="nav-link stat-link fw-bold">
                            <Button color="link" className="nav-link fw-bold" onClick={this.toggleNavbar}>
                                Overview
                            </Button>
                        </LinkContainer>
                    </li>

                    <li className="nav-item mx-2">
                        <UncontrolledButtonDropdown>
                            <DropdownToggle
                                caret
                                color="link"
                                className={`fw-bold stat-link nav-link ${chartsActive ? 'active' : ''}`}
                            >
                                History
                            </DropdownToggle>

                            <DropdownMenu>
                                <DropdownItem tag={Link} to={`${location}/min-max`}>
                                    Min/Max
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/live-charts`}>
                                    Live Charts
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/events`}>
                                    Events
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledButtonDropdown>
                    </li>

                    <li className="nav-item mx-2">
                        <LinkContainer exact to={`${location}/edit`} className="nav-link stat-link fw-bold">
                            <Button color="link" className="nav-link fw-bold" onClick={this.toggleNavbar}>
                                Edit
                            </Button>
                        </LinkContainer>
                    </li>

                    <li className="nav-item mx-2">
                        <LinkContainer exact to="/support/documents" className="nav-link stat-link fw-bold">
                            <Button color="link" className="nav-link fw-bold" onClick={this.setDocumentsSearch}>
                                Manuals
                            </Button>
                        </LinkContainer>
                    </li>

                    <li className="nav-item mx-2">
                        <a
                            href={`/proxy/${this.props.serialNumber}`}
                            target={this.accountStore.user?.statusVuNewTab ? '_blank' : ''}
                            className={this.disabledClass + ' nav-link stat-link fw-bold text-primary'}
                            onClick={this.toggleNavbar}
                        >
                            StatusVu&reg;
                        </a>
                    </li>

                    {this.unit && this.unit.displays.length > 1 && (
                        <li className="nav-item mx-2">
                            <ButtonGroup size="sm">
                                {this.unit.displays.map(d => (
                                    <LinkContainer exact to={`${d}`} key={d}>
                                        <Button disabled={d == this.display?.serialNumber} color="primary">
                                            {d}
                                        </Button>
                                    </LinkContainer>
                                ))}
                            </ButtonGroup>
                        </li>
                    )}
                </ul>

                {this.display && (
                    <TicketModal
                        modalHandle={this.ticketModalHandle}
                        serialNumber={this.display.serialNumber}
                        display={this.display}
                    />
                )}
            </SubTopBar>
        );
    }

    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.serialNumber);
    }

    @computed
    private get disabledClass() {
        if (
            !this.display ||
            !this.display.displayConnectionData ||
            !this.display.displayConnectionData.displayBoardIP ||
            !this.display.displayConnectionData.nocServer
        ) {
            return 'disabled';
        } else return '';
    }

    @computed
    private get unit() {
        if (this.display) return this.unitsStore.units.get(this.display.unitSerialNumber);
    }

    @bind
    @action
    private toggleNavbar() {
        this.collapsed = !this.collapsed;
    }

    @bind
    @action
    private setDocumentsSearch() {
        this.appStore.storage.documentsSearch = this.display?.partNumber || '';
    }

    @observable
    private collapsed = false;
    @inject
    private appStore!: AppStore;
    @inject
    private readonly accountStore!: AccountStore;
    @inject
    private readonly displaysStore!: DisplaysStore;
    @inject
    private readonly unitsStore!: UnitsStore;
    private ticketModalHandle = new ModalHandle();
}
